import React from 'react';
import { VotingProject } from '../components/VoteButton';
import useSessionStorage from '../utils/useSessionStorage';
import { State as UserType, useUserState } from './UserContext';
import { UserGroups } from '../constants/userGroups';

interface ProjectInterface {
  id: number;
  has_voted: boolean;
}

interface PayloadType {
  projects: ProjectInterface[];
  appendData: Function;
  search?: SearchInterface;
  user?: UserType;
}

type PayloadAction = {
  type: 'SAVE_PAYLOAD' | 'REFETCH';
  payload: PayloadType;
};

type RefetchAction = {
  type: 'REFETCH';
  payload: any;
};

type HighlightAction = {
  type: 'HIGHLIGHT_PROJECT';
  payload: number;
};

type VoteAction = {
  type: 'VOTE_PROJECT';
  payload: number;
};
type Action = PayloadAction | RefetchAction | HighlightAction | VoteAction;

type Dispatch = (action: Action) => void;

export type State = {
  projects: any[];
  appendData: Function;
  initialized: boolean;
  isLoading: boolean;
  highlightedProjectId: number | null;
  voteMap: { [key in VotingProject['id']]: boolean };
  search: SearchInterface;
};

type ProjectProviderProps = { children: React.ReactNode };

export type SearchInterface = {
  name?: string;
  atvk_id?: string;
  territorial_units?: string[];
  neighbourhoods?: string[];
  state?: string[];
  category?: string[];
  voter_code?: 'replace';
  submitter_code?: 'replace';
  order?: string;
  has_voted?: boolean;
};

const ProjectContext = React.createContext<State>(undefined!);
const ProjectDispatchContext = React.createContext<Dispatch>(undefined!);
export const VOTE_MAP_SESSION_KEY = 'VOTE_MAP_STATE';

const updateProjectVote = ({ has_voted, ...project }: any) => ({ ...project, has_voted: !has_voted });

const restoreFromStorage = (state: State, projects: any[]) =>
  projects.map((project: any) => ({
    ...project,
    has_voted: state.voteMap[project.id] === undefined ? project.has_voted : state.voteMap[project.id],
  }));

function userReducer(state: State, action: Action, setSessionVoteMap: any) {
  switch (action.type) {
    case 'SAVE_PAYLOAD': {
      const { projects = [], user: { roles = [], selectedRole } = {}, user, ...payload } = action.payload;
      const activeRole = roles.find((e) => e.id === selectedRole);
      const isProxyUser = activeRole?.code === UserGroups.proxy;
      const voteMap = isProxyUser
        ? state.voteMap
        : projects
        ? Object.fromEntries(projects?.map(({ id, has_voted }) => [id, has_voted]))
        : [];

      return {
        ...state,
        ...payload,
        user,
        projects: isProxyUser ? restoreFromStorage(state, projects) : projects ? projects : [],
        initialized: true,
        isLoading: false,
        highlightedProjectId: null,
        voteMap,
      };
    }
    case 'REFETCH': {
      state.appendData(action.payload, undefined, action.payload);
      return {
        ...state,
        projects: [],
        isLoading: true,
        highlightedProjectId: null,
      };
    }
    case 'HIGHLIGHT_PROJECT': {
      return {
        ...state,
        highlightedProjectId: action.payload,
      };
    }

    case 'VOTE_PROJECT': {
      const targetId = action.payload;
      const targetProject = state.projects.find(({ id }: any) => id === targetId);
      const updatedProject = updateProjectVote(targetProject);
      const voteMap = {
        ...state.voteMap,
        [targetId]: updatedProject.has_voted,
      };

      setSessionVoteMap(voteMap);

      return {
        ...state,
        projects: state.projects.map((project) => (project.id === targetId ? updatedProject : project)),
        voteMap,
      };
    }
  }
}

function ProjectProvider({ children }: ProjectProviderProps) {
  const { value, setSessionValue } = useSessionStorage<{ [key in VotingProject['id']]: boolean }>(VOTE_MAP_SESSION_KEY);
  const user = useUserState();

  const initialState = {
    projects: [],
    initialized: false,
    isLoading: true,
    appendData: () => null,
    highlightedProjectId: null,
    voteMap: value || {},
    search: {},
  };

  const [state, baseDispatch] = React.useReducer(
    (prevState: State, action: Action) => userReducer(prevState, action, setSessionValue),
    initialState
  );

  const dispatch = (action: Action) => {
    if (action.type === 'SAVE_PAYLOAD') {
      action.payload = {
        ...action.payload,
        user,
      };
    }

    baseDispatch(action);
  };

  return (
    <ProjectContext.Provider value={state}>
      <ProjectDispatchContext.Provider value={dispatch}>{children}</ProjectDispatchContext.Provider>
    </ProjectContext.Provider>
  );
}

const useProjectState = () => React.useContext(ProjectContext);
const useProjectDispatch = () => React.useContext(ProjectDispatchContext);

export { ProjectProvider, useProjectDispatch, useProjectState };
